import { Injectable } from "@angular/core";
import { LoggerService } from "@bfx/logger";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { AssetClass, BlotterType } from "shared/cubiq-model-multi-asset";
import { IndexType } from "./IndexType";
import { BfxColDef, TableDefinition } from "./TableDefinition";

import { TranslateService } from "@ngx-translate/core";

// real service imports
import { SystemConfigPromiseService } from "shared/auth-services";
import { TraderGroupLookupService } from "../lookup/model/impl/TraderGroupLookupService";
import { UserLookupService } from "../lookup/model/impl/UserLookupService";
import { ProfilePromiseActionService } from "../profile/impl/ProfilePromiseActionService";

// Adapters
import { ColumnFilterType } from "./ColumnFilterType";

import { DealtPricingService } from "../lookup/pricing/impl/DealtPricingService";
import { ProfileSettingKeys } from "./core/ProfileSettingKeys";
import { SysFeatures } from "./core/SysFeatures";

import { ITooltipParams, ValueGetterParams } from "ag-grid-community";
import { ZERO } from "shared/base-components/number-spinner.component";
import {
  BaseBMarkRiskTableDefinitionFactory,
  COLUMN_BMARK_INSTRUMENT,
} from "shared/cubiq-services/model/BaseBmarkRiskTableDefinitionFactory";
import { BenchmarkPositionLookupService } from "../lookup/model/impl/BenchmarkPositionLookupService";
import { TraderGroupPromiseLookupService } from "../lookup/model/impl/TraderGroupPromiseLookupService";
import { BmarkClientOrdersBlotterMode } from "./benchmarks/enums/BmarkClientOrdersBlotterMode.enum";
import { StatusIconsHelper } from "./orders/StatusIconsHelper";
import { COLUMN_ICONS, SPOT_RISK_TABLE_DEFINITION_VER } from "./TableDefinitionFactory.constants";

// COLUMN BMARK DATA NAMES
export const COLUMN_BMARK_TOTAL_INTERNAL: string = "totalInternal";
export const COLUMN_BMARK_TOTAL_INTERNAL_ORDERS: string = "totalInternalOrdersCount";
export const COLUMN_BMARK_TOTAL_EXTERNAL: string = "totalExternal";
export const COLUMN_BMARK_TOTAL_EXTERNAL_ORDERS: string = "totalExternalOrdersCount";

// CLASS NAMES
export const SPOT_RISK_BLOTTER_TYPE = "bmarkSpotRisk";

const logger = LoggerService.for(__dirname);

/**
 * Blotter definition for Benchmark spot  risk - has internal and external group columns,
 * and can be sorted by any column
 */
@Injectable()
export class BmarkSpotRiskTableDefinitionFactory extends BaseBMarkRiskTableDefinitionFactory {
  protected manualRiskRoutingEnabled: boolean = true;
  protected autoHedgingEnabled: boolean;
  protected npvEnabled: boolean;

  protected myStatusIconHelper: StatusIconsHelper;

  constructor(
    profileService: ProfilePromiseActionService,
    // It is not possible to inject against an interface
    traderGroupLookup: TraderGroupLookupService,
    private _sysConfigService: SystemConfigPromiseService,
    private _translateService: TranslateService,
    private _userLookupService: UserLookupService,
    private _myDealtPricingService: DealtPricingService,
    private _benchmarkPositionService: BenchmarkPositionLookupService,
    traderGroupPrimaryLookup: TraderGroupPromiseLookupService,
  ) {
    super(profileService, traderGroupLookup, traderGroupPrimaryLookup);

    // we want connection monitor notifications

    this.myStatusIconHelper = new StatusIconsHelper(
      this._userLookupService,
      this._sysConfigService,
      this._translateService,
      this._myDealtPricingService,
      null, // No TSL support
      this._traderGroupLookup,
    );
  }

  protected isViewTradingGroup(tradingGroup: CubiqTS.TradingGroupImpl): boolean {
    return tradingGroup.isBenchmarkFg;
  }

  // this override for system configs applicable for blotter definition
  protected override getTableCongiPromises(): Array<Promise<any>> {
    return [
      this._sysConfigService.getSystemConfig(SysFeatures.benchmarksEnabled),
      this._sysConfigService.getSystemConfig(SysFeatures.manualRiskRouting),
      this._sysConfigService.getSystemConfig(SysFeatures.bmAutoHedging),
      this._sysConfigService.getSystemConfig(SysFeatures.bmNPV),
    ];
  }

  // this override to set flags returned by promises
  protected override handleTableCongiPromises(values: Array<any>) {
    this.myBmarkEnabled = this.valueOn(values[0]);
    this.manualRiskRoutingEnabled = this.valueOn(values[1]);
    this.autoHedgingEnabled = this.valueOn(values[2]);
    this.npvEnabled = this.valueOn(values[3]);
  }

  public override createDef(): TableDefinition {
    super.createDef();
    logger.out.info(`${this.constructor.name}.createDef()`);

    let position = this.position;
    let td = new TableDefinition("id", COLUMN_BMARK_INSTRUMENT);

    td.benchmarkTableDefinition = true;

    // requestSubmissionTime's column
    //td.setDefaultSort(1, 'asc');

    /****************************************************/
    /* Selection column */
    if (this.manualRiskRoutingEnabled) {
      let selectionColumn: BfxColDef = this.initColdDef();
      selectionColumn.field = "selection";
      selectionColumn.sortingOrder = [];
      selectionColumn.pinned = "left";
      selectionColumn.lockPosition = true;
      selectionColumn.sortable = false;
      selectionColumn.checkboxSelection = true;
      selectionColumn.headerName = "(0)";
      selectionColumn.headerCheckboxSelection = true;
      selectionColumn.headerCheckboxSelectionFilteredOnly = true;
      selectionColumn.suppressMovable = true;
      selectionColumn.suppressResize = false;
      selectionColumn.suppressMenu = true;
      selectionColumn.columnFilterType = ColumnFilterType.NONE;
      selectionColumn.valueGetter = (params: ValueGetterParams) => {
        let icons: string = this.myStatusIconHelper.getIcons(params.data);
        return icons;
      };
      selectionColumn.cellRenderer = function (params) {
        let renderer: string = params.value != null ? params.value : "";
        return renderer;
      };
      selectionColumn.cellClass = ["status-icon-cell", "selection-cell"];
      selectionColumn.width = 58;
      selectionColumn.minWidth = selectionColumn.width;
      selectionColumn.maxWidth = selectionColumn.width;
      selectionColumn.resizable = false;
      selectionColumn.position = this.position.next();
      selectionColumn.omitFromPicker = true;
      selectionColumn.disableInPicker = true;
      selectionColumn.indexType = IndexType.None;

      td.addColumn(selectionColumn);

      /** Manual-Hedge column */
      const manualHedge: BfxColDef = this.initColdDef();
      manualHedge.field = "hedgingMode";
      //  autoHedge.pinned = "left";
      manualHedge.suppressMovable = true;
      manualHedge.lockPosition = true;

      manualHedge.valueGetter = (params) => {
        let hedgingMode = this._benchmarkPositionService.getHedgingMode(params.data.id);
        return (
          params.context.mode() === BmarkClientOrdersBlotterMode.RISK &&
          hedgingMode === CubiqTS.HedgingModeType.MANUAL
        );
      };

      // manualHedge.comparator = (valueA, valueB) => {
      //   const hedgeValueA = valueA.isAutoHedge ? (valueA.isAcceptance ? 2 : 1) : 0;
      //   const hedgeValueB = valueB.isAutoHedge ? (valueB.isAcceptance ? 2 : 1) : 0;
      //   return hedgeValueA - hedgeValueB;
      // };
      manualHedge.headerName = "MH";
      manualHedge.sortable = true;
      manualHedge.resizable = true;
      manualHedge.suppressMenu = true;
      manualHedge.columnFilterType = ColumnFilterType.NONE;
      //manualHedge.cellRenderer = "autoHedgeRenderer";
      manualHedge.tooltipValueGetter = (value) => {
        let hedgingMode = this._benchmarkPositionService.getHedgingMode(value.data.id);
        let tooltip: string = "";

        if (hedgingMode === CubiqTS.HedgingModeType.MANUAL) {
          let userId = this._benchmarkPositionService.getHedgingModeChangeUserId(value.data.id);
          if (userId != null) {
            let user = this._userLookupService.getUserById(userId.toString());
            let userStr = this.showUserName ? user?.userName : user?.userCode;
            tooltip = this._translateService.instant("bm.position.hedge.mode.tooltip", [userStr]);
          }
        }

        return tooltip;
      };

      manualHedge.cellRenderer = (params) =>
        params.value == true ? '<span class="badge-icon bfx-badge-negative-bold">MH</span>' : "";

      manualHedge.width = 50;
      manualHedge.minWidth = this.twoWs;
      manualHedge.maxWidth = 50;
      manualHedge.position = this.position.next();
      manualHedge.omitFromPicker = true;
      manualHedge.disableInPicker = true;

      manualHedge.headerTooltip = "Manual Hedging Requested";

      td.addColumn(manualHedge);
      logger.out.info(
        "[" +
          selectionColumn.position +
          `] : Added Selection Column as this.manualRiskRoutingEnabled is true`,
      );
    } else {
      logger.out.info("No Selection Column as this.manualRiskRoutingEnabled is false");
    }

    if (this.npvEnabled) {
      let additionalIcons: BfxColDef = this.initColdDef();
      additionalIcons.field = COLUMN_ICONS;
      additionalIcons.sortingOrder = [];
      additionalIcons.pinned = "left";
      additionalIcons.lockPosition = true;
      additionalIcons.sortable = false;
      additionalIcons.headerName = "";
      additionalIcons.suppressMovable = true;
      additionalIcons.suppressResize = false;
      additionalIcons.suppressMenu = true;
      additionalIcons.columnFilterType = ColumnFilterType.NONE;
      additionalIcons.valueGetter = function (params: ValueGetterParams) {
        let icons: string = this.myStatusIconHelper.getNPVIconForSpotRisk(params.data);
        return icons;
      }.bind(this);
      additionalIcons.cellRenderer = function (params) {
        let renderer: string = params.value != null ? params.value : "";
        return renderer;
      };
      additionalIcons.cellClass = ["status-icon-cell", "selection-cell"];
      additionalIcons.width = 35;
      additionalIcons.minWidth = additionalIcons.width;
      additionalIcons.maxWidth = additionalIcons.width;
      additionalIcons.resizable = false;
      additionalIcons.position = this.position.next();
      additionalIcons.omitFromPicker = true;
      additionalIcons.disableInPicker = true;
      additionalIcons.indexType = IndexType.None;

      td.addColumn(additionalIcons);
    }

    /****************************************************/
    /* INSTRUMENT (Currency pair) */
    const instrument: BfxColDef = this.createInstrumentCol();
    td.addColumn(instrument);

    /****************************************************/
    // Total
    let total: BfxColDef = this.createTotalCol();
    td.addColumn(total);

    /****************************************************/
    // Total Internal
    let totalInternal: BfxColDef = this.initColdDef();
    totalInternal.headerName = "Total (Internal)";
    totalInternal.field = COLUMN_BMARK_TOTAL_INTERNAL;
    totalInternal.indexType = IndexType.NaturalString;
    totalInternal.minWidth = this.sixWs;
    totalInternal.width = this.elevenWs;
    totalInternal.maxWidth = this.twentyWs;
    totalInternal.isPrimary = true; // adds 'blotter_primary_col' class
    totalInternal.cellClass = ["blotter_spot-risk_total"];
    //totalInternal.valueFormatter = this.formatAmts;
    totalInternal.columnFilterType = ColumnFilterType.INT;
    totalInternal.columnFilterData = null;
    totalInternal.valueGetter = function (params) {
      if (
        params != null &&
        params.data != null &&
        params.data[COLUMN_BMARK_TOTAL_INTERNAL] instanceof CubiqTS.BigNumber
      ) {
        return params.data[COLUMN_BMARK_TOTAL_INTERNAL].toNumber();
      } else {
        return 0;
      }
    }.bind(this);
    totalInternal.tooltipValueGetter = (params: ITooltipParams) => {
      const value: CubiqTS.BigNumber = params.data?.[COLUMN_BMARK_TOTAL_INTERNAL] ?? ZERO;
      return value.toFormat(params.data?.fullPrecision ?? 0);
    };
    totalInternal.cellRenderer = "bmarkSpotRiskTotalsRenderer";
    totalInternal.cellRendererParams = {
      columnProperty: COLUMN_BMARK_TOTAL_INTERNAL,
      orderCountProperty: COLUMN_BMARK_TOTAL_INTERNAL_ORDERS,
      displayEmpty: this.displayEmpty,
    };

    if (!this.restoreFromConfig(totalInternal)) {
      totalInternal.position = this.position.next();
    }

    td.addColumn(totalInternal);

    /****************************************************/
    // Total external
    let totalExternal: BfxColDef = this.initColdDef();
    totalExternal.headerName = "Total (External)";
    totalExternal.field = COLUMN_BMARK_TOTAL_EXTERNAL;
    totalExternal.indexType = IndexType.NaturalString;
    totalExternal.minWidth = this.sixWs;
    totalExternal.width = this.elevenWs;
    totalExternal.maxWidth = this.twentyWs;
    totalExternal.isPrimary = true; // adds 'blotter_primary_col' class
    totalExternal.cellClass = ["blotter_spot-risk_total"];
    //totalExternal.cellClassRules = {
    //  externalColColour: (params: CellClassParams) =>
    //    params.data.externalColoursEnabled == true,
    //};
    //totalExternal.valueFormatter = this.formatAmts;
    totalExternal.columnFilterType = ColumnFilterType.INT;
    totalExternal.columnFilterData = null;
    totalExternal.valueGetter = function (params) {
      if (
        params != null &&
        params.data != null &&
        params.data[COLUMN_BMARK_TOTAL_EXTERNAL] instanceof CubiqTS.BigNumber
      ) {
        return params.data[COLUMN_BMARK_TOTAL_EXTERNAL].toNumber();
      } else {
        return 0;
      }
    }.bind(this);
    totalExternal.tooltipValueGetter = (params: ITooltipParams) => {
      const value: CubiqTS.BigNumber = params.data?.[COLUMN_BMARK_TOTAL_EXTERNAL] ?? ZERO;
      return value.toFormat(params.data?.fullPrecision ?? 0);
    };
    totalExternal.cellRenderer = "bmarkSpotRiskTotalsRenderer";
    totalExternal.cellRendererParams = {
      columnProperty: COLUMN_BMARK_TOTAL_EXTERNAL,
      orderCountProperty: COLUMN_BMARK_TOTAL_EXTERNAL_ORDERS,
      displayEmpty: this.displayEmpty,
    };
    if (!this.restoreFromConfig(totalExternal)) {
      totalExternal.position = this.position.next();
    }

    td.addColumn(totalExternal);

    const groupCols = this.createTraderGroupCols(this.bmarkTradingGroups);
    for (const col of groupCols) {
      td.addColumn(col);
    }
    return td;
  }

  public getBlotterType() {
    return BlotterType.BENCHMARK;
  }

  public getAssetClass() {
    return AssetClass.FIX;
  }

  public getBlotterVersion(): string {
    return this.getVersionIdentifier();
  }

  public getVersionIdentifier(): string {
    return SPOT_RISK_TABLE_DEFINITION_VER;
  }

  protected getProfileSettingKey(): string {
    return `${ProfileSettingKeys.userProfileDefaultBlotter}${SPOT_RISK_BLOTTER_TYPE}`;
  }
}
